import { memo } from 'react';
import { Box } from '@mui/material';

interface Partner {
  name: string;
  src: string;
}

interface LogoMarqueeProps {
  partners: Partner[];
  speed?: number;
  animate?: boolean;
}

function LogoMarqueeComponent({ partners, speed = 30, animate = true }: LogoMarqueeProps) {
  // Duplicate the array for seamless loop only when animating
  const displayPartners = animate ? [...partners, ...partners] : partners;

  return (
    <Box
      sx={{
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: { xs: 40, md: 100 },
          zIndex: 2,
          pointerEvents: 'none',
        },
        '&::before': {
          left: 0,
          background: 'linear-gradient(to right, white, transparent)',
        },
        '&::after': {
          right: 0,
          background: 'linear-gradient(to left, white, transparent)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 4, md: 6 },
          ...(animate && {
            animation: `marquee ${speed}s linear infinite`,
            width: 'max-content',
            '@keyframes marquee': {
              '0%': {
                transform: 'translateX(0)',
              },
              '100%': {
                transform: 'translateX(-50%)',
              },
            },
            '&:hover': {
              animationPlayState: 'paused',
            },
          }),
          ...(!animate && {
            justifyContent: 'center',
            flexWrap: 'wrap',
          }),
        }}
      >
        {displayPartners.map((partner, index) => (
          <Box
            key={`${partner.name}-${index}`}
            component="img"
            src={partner.src}
            alt={partner.name}
            loading="lazy"
            decoding="async"
            sx={{
              height: { xs: 32, md: 40 },
              width: 'auto',
              maxWidth: { xs: 100, md: 140 },
              objectFit: 'contain',
              filter: 'grayscale(100%)',
              opacity: 0.7,
              transition: 'all 0.3s ease',
              flexShrink: 0,
              '&:hover': {
                filter: 'grayscale(0%)',
                opacity: 1,
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

export const LogoMarquee = memo(LogoMarqueeComponent);
